import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, FlaskConical, ArrowRight } from 'lucide-react';

interface Hypothesis {
  id: string;  // H1, H2, etc.
  statement: string;
  rationale: string;
  data_needed_to_test: string[];
  priority: 'high' | 'medium' | 'low';
  expected_impact?: string;
}

interface HypothesesSectionProps {
  hypotheses: Hypothesis[];
  className?: string;
}

const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const styles = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-blue-100 text-blue-700 border-blue-200'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border capitalize ${styles[priority as keyof typeof styles]}`}>
      {priority} Priority
    </span>
  );
};

const HypothesisCard: React.FC<{ hypothesis: Hypothesis; index: number }> = ({ hypothesis, index }) => {
  const [expanded, setExpanded] = useState(index === 0); // First one expanded by default

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-bold text-sm flex-shrink-0">
            {hypothesis.id}
          </span>
          <div className="text-left">
            <p className="font-medium text-neutral-900 text-sm leading-snug">
              {hypothesis.statement}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          <PriorityBadge priority={hypothesis.priority} />
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="p-4 pt-0 border-t border-neutral-100 space-y-3">
          {/* Rationale */}
          {hypothesis.rationale && (
            <div>
              <p className="text-xs font-medium text-neutral-600 mb-1 flex items-center gap-1">
                <Lightbulb className="w-3 h-3" />
                Why This Matters
              </p>
              <p className="text-sm text-neutral-700">{hypothesis.rationale}</p>
            </div>
          )}

          {/* Data Needed */}
          {hypothesis.data_needed_to_test && hypothesis.data_needed_to_test.length > 0 && (
            <div>
              <p className="text-xs font-medium text-neutral-600 mb-1 flex items-center gap-1">
                <FlaskConical className="w-3 h-3" />
                Data Needed to Test
              </p>
              <ul className="space-y-1">
                {hypothesis.data_needed_to_test.map((data, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-600">
                    <ArrowRight className="w-3 h-3 mt-1 flex-shrink-0 text-neutral-400" />
                    <span>{data}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Expected Impact */}
          {hypothesis.expected_impact && (
            <div className="pt-2 border-t border-neutral-100">
              <p className="text-xs font-medium text-neutral-600 mb-1">Expected Impact if Validated</p>
              <p className="text-sm text-neutral-700 italic">{hypothesis.expected_impact}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const HypothesesSection: React.FC<HypothesesSectionProps> = ({
  hypotheses,
  className = ''
}) => {
  if (!hypotheses || hypotheses.length === 0) {
    return null;
  }

  // Sort hypotheses by ID (H1, H2, H3...) to maintain logical sequence
  const sortedHypotheses = [...hypotheses].sort((a, b) => {
    // Extract numeric part from ID (e.g., "H1" -> 1, "H2" -> 2)
    const getIdNumber = (id: string): number => {
      const match = id.match(/\d+/);
      return match ? parseInt(match[0], 10) : 999;
    };
    return getIdNumber(a.id) - getIdNumber(b.id);
  });

  const highPriority = hypotheses.filter(h => h.priority === 'high').length;
  const mediumPriority = hypotheses.filter(h => h.priority === 'medium').length;
  const lowPriority = hypotheses.filter(h => h.priority === 'low').length;

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-neutral-900">Key Hypotheses to Test</h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {highPriority > 0 && (
            <span className="px-2 py-1 bg-red-50 text-red-600 rounded">
              {highPriority} High
            </span>
          )}
          {mediumPriority > 0 && (
            <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded">
              {mediumPriority} Medium
            </span>
          )}
          {lowPriority > 0 && (
            <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded">
              {lowPriority} Low
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-neutral-600 mb-4">
        These hypotheses should be tested during the analysis to validate or invalidate
        key assumptions about the business problem.
      </p>

      <div className="space-y-3">
        {sortedHypotheses.map((hypothesis, index) => (
          <HypothesisCard key={hypothesis.id} hypothesis={hypothesis} index={index} />
        ))}
      </div>
    </div>
  );
};

export default HypothesesSection;
